import { motion } from 'motion/react'
import Flag from 'react-world-flags'
import { type Plan, parsePlanSpecs, locationInfo } from '../types'

interface PlanCardProps {
  plan: Plan
  /** Delay en segundos para stagger animation (multiplica por idx). */
  delayMultiplier?: number
  /** Si true, no anima al montar (útil en grids que cambian por tabs). */
  disableInitialAnimation?: boolean
}

/**
 * Card visual de un plan. Muestra región, specs (RAM/vCPUs/SSD parseadas
 * desde `description`), precio y CTA. Reusable fuera de PlansSection:
 * ej. billing del dashboard, página de upgrade, resumen de plan actual.
 *
 * Usa `react-world-flags` (mismo paquete que ServerPanel.tsx) para
 * mostrar la bandera del país en lugar de emojis Unicode.
 */
export function PlanCard({
  plan,
  delayMultiplier = 0.06,
  disableInitialAnimation = false,
}: PlanCardProps) {
  const specs = parsePlanSpecs(plan.description)
  const loc = locationInfo(plan.location)

  return (
    <motion.div
      className="plan-card"
      initial={disableInitialAnimation ? false : { opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: delayMultiplier }}
    >
      <div className="plan-region">
        <Flag
          code={loc.countryCode}
          className="plan-flag-img"
          fallback={<span className="plan-flag-fallback">{loc.countryCode}</span>}
          aria-label={loc.region}
        />
        <div>
          <div className="plan-region-name">{loc.region}</div>
          <div className="plan-location">{loc.location}</div>
        </div>
      </div>

      <ul className="plan-specs">
        <li><span>RAM</span><strong>{specs.ram}</strong></li>
        <li><span>vCPUs</span><strong>{specs.vcpu}</strong></li>
        <li><span>Almacenamiento</span><strong>{specs.ssd}</strong></li>
      </ul>

      <div className="plan-price">
        <span className="plan-currency">$</span>
        <span className="plan-amount">{plan.price.toFixed(3)}</span>
        <span className="plan-period">/hora</span>
      </div>

      <p className="plan-note">
        Solo pagas por las horas que la sesión esté encendida.
      </p>
    </motion.div>
  )
}